/* global process, URL */
/**
 * Image resolver for the media pipeline (see s3-costing/IMAGE_MIGRATION_PLAN.md §2.6).
 *
 * Every entity that has an image field also has a `*V2` field with entries
 *   { src, key, w, h, status, url? }
 * joined to the legacy URL by `src`. When an entry is `ready` and the flag is on, we render
 * pre-generated variants from CloudFront:
 *   <CDN>/<key>/256.avif  <CDN>/<key>/512.avif  <CDN>/<key>/512.webp  <CDN>/<key>/orig.avif
 * Otherwise we return today's URL (Azure resize URL for legacy Azure images, CloudFront via
 * toCdnUrl for S3 images), so a missing/pending entry changes nothing.
 */
import { getCdnHost, toCdnUrl } from "./cdn";

export const isMediaV2Enabled = () => process.env.NEXT_PUBLIC_MEDIA_V2_ENABLED === "true";

const cdnBase = () => {
  const host = getCdnHost();
  return host ? `https://${host}` : "";
};

/**
 * Per-slot candidate lists. Browsers pick a candidate by CSS size × device pixel ratio, so the
 * grid never offers `orig` (a 3× phone would otherwise fetch the full image for a thumbnail).
 */
export const SLOTS = {
  // Product tile and featured-collection card share a box: `calc(50vw - 33px)` under the 768px
  // breakpoint, then a fixed `calc(520px/2 - 33px)` = 227px once the 520px container caps out.
  // Declaring the real box (not a viewport fraction) is what keeps the pick stable: 512 for every
  // DPR above 1, and 256 only on a genuinely low-DPR screen, where 256 physical px already covers
  // the box. A viewport fraction made the choice track window width on desktop even though the
  // tile never changes size.
  grid: { sizes: "(max-width: 768px) calc(50vw - 33px), 227px", avif: [512, 256] },
  // Collection-home / similar-products tile: `var(--max-width)/1.5 - 50px` = 297px at every width.
  gridWide: { sizes: "297px", avif: [512, 256] },
  pdp: { sizes: "(max-width: 768px) 100vw, 480px", avif: [512, "orig"] },
  cart: { sizes: "96px", avif: [256] },
  logo: { sizes: "120px", avif: [256] },
  hero: { sizes: "100vw", avif: [512, "orig"] },
  sidebar: { sizes: "70px", avif: [256] },
  collection: { sizes: "(max-width: 768px) calc(50vw - 33px), 227px", avif: [512, 256] },
  review: { sizes: "250px", avif: [512] },
};

/** Legacy Azure hosts the storefront still rewrites (unchanged behaviour). */
const AZURE_RESIZE = "https://dm2buy-resize-dynamic-cebdcaefgydgh6hu.z02.azurefd.net/dm2buy/";
const AZURE_DROP = "https://dm2buy-drop-resized-gga4c6azekgcgngp.z02.azurefd.net/dm2buy/";

/** Today's guard, made safe for the new bucket: an S3 URL is never an Azure URL. */
export const isAzureLegacy = (url) =>
  typeof url === "string" && url.includes("dm2buy") && !url.includes("amazonaws.com");

const fileName = (url) => {
  try {
    return new URL(url).pathname.split("/").pop().split("?")[0];
  } catch {
    return String(url || "")
      .split("/")
      .pop()
      .split("?")[0];
  }
};
const baseName = (url) => {
  const f = fileName(url);
  const i = f.lastIndexOf(".");
  return i > 0 ? f.substring(0, i) : f;
};

/**
 * Exactly what each component renders today for a legacy URL, by slot.
 */
export function legacyUrl(src, slot = "grid") {
  if (!src) return src;
  if (isAzureLegacy(src)) {
    switch (slot) {
      case "grid":
      case "gridWide":
        return `${AZURE_RESIZE}${baseName(src)}.jpg?width=400&height=400`;
      case "pdp":
        return `${AZURE_DROP}${fileName(src)}`;
      case "cart":
      case "logo":
        return `${AZURE_RESIZE}${baseName(src)}.jpg?quality=50`;
      case "hero":
        return `${AZURE_RESIZE}${fileName(src)}?width=500&quality=80`;
      default:
        return src;
    }
  }
  return toCdnUrl(src);
}

const findV2 = (src, v2) => {
  if (!src || !v2) return null;
  if (Array.isArray(v2)) return v2.find((m) => m && m.src === src && m.status === "ready" && m.key);
  return v2.src === src && v2.status === "ready" && v2.key ? v2 : null;
};

/**
 * `w`/`h` are optional in the backend schema, so a `ready` entry can arrive without them. Every
 * width descriptor would then be `0w`, which invalidates the whole AVIF srcSet (the browser drops
 * the candidates and skips the <source>) and leaves width/height unset, losing the layout-shift
 * reservation. Such an entry is not usable as V2: fall back to today's URL instead.
 */
const hasDimensions = (entry) =>
  Number.isFinite(entry?.w) && entry.w > 0 && Number.isFinite(entry?.h) && entry.h > 0;

/**
 * Resolve one image for a slot.
 * @param {string} src            the legacy URL (value of productPhotos[i], store.logo, ...)
 * @param {Array|Object} v2       the matching *V2 field (array for lists, object for singles)
 * @param {keyof SLOTS} slot
 * @returns {{ isV2: boolean, src: string, fallbacks: string[], sources?: Array, width?: number, height?: number, sizes?: string }}
 *   `src` is what goes in <img src>; `fallbacks` are tried in order by the onError handler.
 */
export function resolveImage(src, v2, slot = "grid") {
  const cfg = SLOTS[slot] || SLOTS.grid;
  const entry = isMediaV2Enabled() ? findV2(src, v2) : null;
  const base = cdnBase();

  if (entry && base && hasDimensions(entry)) {
    const u = (name) => `${base}/${entry.key}/${name}`;
    const w = entry.w;
    const h = entry.h;
    const L = Math.max(w, h);
    // width descriptor = the variant's real width (256/512 are long-edge caps; portrait 1080×1440 → 384w)
    const widthOf = (t) => (t === "orig" ? w : Math.round((Math.min(t, L) * w) / L));
    return {
      isV2: true,
      src: u("512.webp"),
      width: w,
      height: h,
      sizes: cfg.sizes,
      sources: [
        {
          type: "image/avif",
          srcSet: cfg.avif.map((t) => `${u(`${t}.avif`)} ${widthOf(t)}w`).join(", "),
          sizes: cfg.sizes,
        },
      ],
      // if the CDN variant fails (or the browser decodes neither AVIF nor WebP), fall back to
      // today's URL, then the raw stored string: both are the original image for every source
      fallbacks: [legacyUrl(src, slot), src].filter((x, i, arr) => x && x !== u("512.webp") && arr.indexOf(x) === i),
    };
  }

  const legacy = legacyUrl(src, slot);
  return {
    isV2: false,
    src: legacy,
    fallbacks: legacy !== src ? [src] : [],
  };
}

/**
 * og:image for link previews. Crawlers do not negotiate AVIF; 512.webp is accepted by Meta and
 * WhatsApp. Falls back to today's CDN URL of the original.
 */
export function ogImage(src, v2) {
  if (!src) return src;
  const entry = isMediaV2Enabled() ? findV2(src, v2) : null;
  const base = cdnBase();
  if (entry && base) return `${base}/${entry.key}/512.webp`;
  if (src.startsWith("//")) return `https:${src}`;
  return toCdnUrl(src);
}
